import React, { useEffect, useState, useCallback } from 'react';
import AdminShell from './AdminShell';
import { useAdminI18n } from './i18n';
import { getMe, getOverview, getMonthly, getByUser, getFeedback } from './adminApi';
import './admin.css';

const PIE_COLORS = [
  '#FF0000', '#CC0000', '#2ABDBB', '#008C8B', '#FF8A4D',
  '#F5A623', '#990000', '#1C8A4D', '#7B848A', '#5F686D',
];

const Metric = ({ label, value, sub }) => (
  <div className="metric-card">
    <div className="metric-label">{label}</div>
    <div className="metric-value">{value}</div>
    {sub != null && <div className="metric-sub">{sub}</div>}
  </div>
);

const Bar = ({ label, value, max, alt }) => {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="bar-row">
      <div className="bar-label" title={label}>{label}</div>
      <div className="bar-track">
        <div className={`bar-fill ${alt ? 'alt' : ''}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="bar-value">{value}</div>
    </div>
  );
};

/** Interactive SVG donut + legend. Hover a slice/legend row to spotlight it and
 *  show that slice's value & % in the center. data: [{label, value}]. */
const PieChart = ({ data, centerLabel = 'total' }) => {
  const { t } = useAdminI18n();
  const [hover, setHover] = useState(null);
  const slices = (data || []).filter((d) => d.value > 0);
  const total = slices.reduce((s, d) => s + d.value, 0);

  if (!total) return <div className="admin-empty">{t('No data to chart.')}</div>;

  const R = 42;
  const C = 2 * Math.PI * R;
  let acc = 0;
  const segs = slices.map((d, i) => {
    const frac = d.value / total;
    const seg = {
      i,
      label: d.label,
      value: d.value,
      pct: Math.round(frac * 100),
      color: PIE_COLORS[i % PIE_COLORS.length],
      dash: frac * C,
      gap: C - frac * C,
      offset: -(acc / total) * C,
    };
    acc += d.value;
    return seg;
  });

  const active = hover != null ? segs[hover] : null;

  return (
    <div className="pie-wrap">
      <div className="pie-svg-wrap">
        <svg viewBox="0 0 100 100" className={`pie-svg ${active ? 'has-hover' : ''}`}>
          <g transform="rotate(-90 50 50)">
            {segs.map((s) => (
              <circle
                key={s.i}
                cx="50" cy="50" r={R} fill="none"
                stroke={s.color}
                strokeWidth={hover === s.i ? 19 : 16}
                strokeDasharray={`${s.dash} ${s.gap}`}
                strokeDashoffset={s.offset}
                className={`pie-seg ${hover === s.i ? 'on' : ''}`}
                onMouseEnter={() => setHover(s.i)}
                onMouseLeave={() => setHover(null)}
              />
            ))}
          </g>
          <text x="50" y="49" textAnchor="middle" className="pie-svg-total">
            {active ? active.value : total}
          </text>
          <text x="50" y="59" textAnchor="middle" className="pie-svg-sub">
            {active ? `${active.pct}%` : centerLabel}
          </text>
        </svg>
      </div>
      <div className="pie-legend">
        {segs.map((s) => (
          <div
            className={`pie-legend-item ${hover === s.i ? 'hl' : ''}`}
            key={s.label}
            onMouseEnter={() => setHover(s.i)}
            onMouseLeave={() => setHover(null)}
          >
            <span className="pie-dot" style={{ background: s.color }} />
            <span className="pie-legend-label" title={s.label}>{s.label}</span>
            <span className="pie-legend-val">{s.value} · {s.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const topNWithOthers = (rows, n, labelKey, valueKey, othersLabel) => {
  const sorted = [...rows].sort((a, b) => b[valueKey] - a[valueKey]);
  const head = sorted.slice(0, n).map((r) => ({ label: r[labelKey], value: r[valueKey] }));
  const rest = sorted.slice(n).reduce((s, r) => s + r[valueKey], 0);
  if (rest > 0) head.push({ label: othersLabel, value: rest });
  return head;
};

const fmt = (d) => {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'month', label: 'This month' },
  { key: 'year', label: 'This year' },
  { key: 'all', label: 'All time' },
];

const AnalyticsDashboard = () => {
  const { t } = useAdminI18n();
  const [authState, setAuthState] = useState('checking');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [preset, setPreset] = useState('all');
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [months, setMonths] = useState([]);
  const [byUser, setByUser] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async (s, e) => {
    setLoading(true);
    setError(null);
    try {
      const [ov, mo, bu, fb] = await Promise.all([
        getOverview(s, e),
        getMonthly(s, e),
        getByUser(s, e, 30),
        getFeedback(s, e),
      ]);
      setOverview(ov);
      setMonths(mo.months || []);
      setByUser(bu.users || []);
      setFeedback(fb);
    } catch (err) {
      setError(t('Failed to load analytics.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    getMe()
      .then((d) => {
        if (d && d.is_admin && d.is_active) {
          setAuthState('ok');
          loadAll('', '');
        } else {
          setAuthState('denied');
        }
      })
      .catch(() => setAuthState('denied'));
  }, [loadAll]);

  const applyPreset = (key) => {
    const now = new Date();
    let s = '';
    let e = fmt(now);
    if (key === 'today') s = fmt(now);
    else if (key === '7d') { const d = new Date(now); d.setDate(d.getDate() - 6); s = fmt(d); }
    else if (key === '30d') { const d = new Date(now); d.setDate(d.getDate() - 29); s = fmt(d); }
    else if (key === 'month') s = fmt(new Date(now.getFullYear(), now.getMonth(), 1));
    else if (key === 'year') s = fmt(new Date(now.getFullYear(), 0, 1));
    else if (key === 'all') { s = ''; e = ''; }
    setPreset(key);
    setStart(s);
    setEnd(e);
    loadAll(s, e);
  };

  const applyManual = () => {
    setPreset('custom');
    loadAll(start, end);
  };

  if (authState === 'checking') {
    return <AdminShell title={t('Analytics')}><div className="admin-loading">{t('Checking access…')}</div></AdminShell>;
  }
  if (authState === 'denied') {
    return (
      <AdminShell title={t('Analytics')}>
        <div className="admin-denied">
          <h2>{t('Access denied')}</h2>
          <p>{t('You need admin rights to view analytics.')}</p>
        </div>
      </AdminShell>
    );
  }

  const maxChats = Math.max(1, ...months.map((m) => m.chats || 0));
  const maxMsgs = Math.max(1, ...months.map((m) => m.messages || 0));

  const userPie = topNWithOthers(byUser, 7, 'user', 'chats', t('Others'));
  const ratingPie = feedback
    ? ['5', '4', '3', '2', '1'].map((s) => ({ label: `${s} ★`, value: feedback.rating_distribution?.[s] || 0 }))
    : [];
  const votePie = feedback
    ? Object.entries(feedback.votes || {}).map(([k, v]) => ({ label: k, value: v }))
    : [];

  return (
    <AdminShell title={t('Analytics')}>
      {/* Quick filters + date range */}
      <div className="filter-card">
        <div className="quick-filters">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              className={`quick-chip ${preset === p.key ? 'active' : ''}`}
              onClick={() => applyPreset(p.key)}
            >
              {t(p.label)}
            </button>
          ))}
        </div>
        <div className="admin-toolbar" style={{ marginBottom: 0 }}>
          <div className="admin-field">
            <label>{t('From')}</label>
            <input type="date" className="admin-input date" value={start}
              onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="admin-field">
            <label>{t('To')}</label>
            <input type="date" className="admin-input date" value={end}
              onChange={(e) => setEnd(e.target.value)} />
          </div>
          <button className="admin-btn" onClick={applyManual} disabled={loading}>
            {loading ? t('Loading…') : t('Apply filter')}
          </button>
        </div>
      </div>

      {error && <div className="admin-banner error">{error}</div>}

      {/* Headline metrics */}
      <div className="metric-grid">
        <Metric label={t('Total Chats')} value={overview?.total_chats ?? '—'} />
        <Metric label={t('Total Messages')} value={overview?.total_messages ?? '—'}
          sub={overview ? `${overview.avg_messages_per_chat} ${t('avg / chat')}` : null} />
        <Metric label={t('Active Users')} value={overview?.active_users ?? '—'} sub={t('in selected range')} />
        <Metric label={t('Registered Users')} value={overview?.registered_users ?? '—'}
          sub={overview ? `${overview.admins} ${t('admins')}` : null} />
        <Metric label={t('Message Feedback')} value={feedback?.total_message_feedback ?? '—'} />
        <Metric label={t('App Rating')} value={feedback?.avg_rating ?? '—'}
          sub={feedback ? `${feedback.total_app_feedback} ${t('ratings')}` : null} />
      </div>

      {/* Pie row: user share + rating distribution */}
      <div className="chart-grid">
        <div className="admin-panel">
          <h2>{t('Chat share by user')}</h2>
          <div className="chart-body">
            <PieChart data={userPie} centerLabel={t('chats')} />
          </div>
        </div>
        <div className="admin-panel">
          <h2>{t('App rating distribution')}</h2>
          <div className="chart-body">
            <PieChart data={ratingPie} centerLabel={t('ratings')} />
          </div>
        </div>
      </div>

      {/* Message votes + monthly trend, side by side */}
      <div className="chart-grid">
        <div className="admin-panel">
          <h2>{t('Message votes')}</h2>
          <div className="chart-body">
            <PieChart data={votePie} centerLabel={t('votes')} />
          </div>
        </div>

        <div className="admin-panel">
          <h2>{t('Monthly activity')}</h2>
          <div className="chart-body">
            {months.length === 0 ? (
              <div className="admin-empty">{t('No data for this range.')}</div>
            ) : (
              <>
                <div className="bar-legend">
                  <span><span className="dot primary" />{t('Chats')}</span>
                  <span><span className="dot secondary" />{t('Messages')}</span>
                </div>
                {months.map((m) => (
                  <div key={m.month} className="month-group">
                    <Bar label={`${m.month} · ${t('chats')}`} value={m.chats} max={maxChats} />
                    <Bar label={`${m.month} · ${t('Messages')}`} value={m.messages} max={maxMsgs} alt />
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Recent feedback — always shown, with an empty state instead of vanishing */}
      <div className="admin-panel">
        <h2>{t('Recent feedback')}</h2>
        {feedback && feedback.recent && feedback.recent.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr><th>{t('User')}</th><th>{t('Vote')}</th><th>{t('Comment')}</th><th>{t('When')}</th></tr>
              </thead>
              <tbody>
                {feedback.recent.map((r, i) => (
                  <tr key={i}>
                    <td>{r.user || '—'}</td>
                    <td>{r.vote || '—'}</td>
                    <td style={{ maxWidth: 380, whiteSpace: 'pre-wrap' }}>{r.feedback || '—'}</td>
                    <td>{r.timestamp ? String(r.timestamp).slice(0, 10) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-empty">{t('No feedback in this range yet.')}</div>
        )}
      </div>
    </AdminShell>
  );
};

export default AnalyticsDashboard;
